import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Container } from '../common/Container';
import { Button } from '../common/Button';
import { DINOSAUR_ASCII_SMALL } from '../../assets/dinosaur-ascii';
import { useAuth } from '../../contexts/AuthContext';

const HeaderWrapper = styled.header`
  background-color: ${theme.colors.background};
  border-bottom: 1px solid ${theme.colors.borderLight};
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.9);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.md} 0;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  text-decoration: none;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;

  &:hover {
    text-decoration: none;
    color: ${theme.colors.primary};
  }
`;

const LogoIcon = styled.span`
  font-size: ${theme.fontSizes['2xl']};
  line-height: 1;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xl};
`;

const NavLinks = styled.div`
  display: none;
  align-items: center;
  gap: ${theme.spacing.lg};

  @media (min-width: ${theme.breakpoints.md}) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  color: ${theme.colors.text.secondary};
  font-weight: 500;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary};
    text-decoration: none;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.md};
  color: ${theme.colors.text.primary};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary};
  }
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Dropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + ${theme.spacing.xs});
  right: 0;
  min-width: 200px;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.lg};
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? '0' : '-10px'});
  transition: all ${theme.transitions.fast};
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${theme.colors.text.secondary};
  font-weight: 500;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
    color: ${theme.colors.primary};
    text-decoration: none;
  }

  &:first-child {
    border-radius: ${theme.radii.md} ${theme.radii.md} 0 0;
  }

  &:last-child {
    border-radius: 0 0 ${theme.radii.md} ${theme.radii.md};
  }
`;

const DropdownButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
    color: ${theme.colors.error};
  }

  &:last-child {
    border-radius: 0 0 ${theme.radii.md} ${theme.radii.md};
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: ${theme.colors.borderLight};
  margin: ${theme.spacing.xs} 0;
`;

const UserEmail = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  const handleLogout = async () => {
    await logout();
    setIsDropdownOpen(false);
    navigate('/');
  };
  
  const getInitial = (email: string) => {
    return email ? email[0].toUpperCase() : 'U';
  };
  
  return (
    <HeaderWrapper>
      <Container>
        <HeaderContent>
          <Logo to="/">
            <LogoIcon>{DINOSAUR_ASCII_SMALL}</LogoIcon>
            <span>Promptasaurus</span>
          </Logo>
          <Nav>
            <NavLinks>
              <NavLink to="/fossil_fills">Fossil Fills</NavLink>
              <NavLink to="/demo">Demo</NavLink>
              <NavLink to="/pricing">Pricing</NavLink>
              <NavLink to="/docs">Documentation</NavLink>
              <NavLink to="/about">About</NavLink>
            </NavLinks>
            <Actions>
              {user ? (
                <UserMenu ref={dropdownRef}>
                  <UserButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    <UserAvatar>{getInitial(user.email)}</UserAvatar>
                    <span>Account</span>
                  </UserButton>
                  <Dropdown isOpen={isDropdownOpen}>
                    <UserEmail>{user.email}</UserEmail>
                    <DropdownItem to="/dashboard" onClick={() => setIsDropdownOpen(false)}>
                      Dashboard
                    </DropdownItem>
                    <DropdownItem to="/settings" onClick={() => setIsDropdownOpen(false)}>
                      Settings
                    </DropdownItem>
                    <DropdownDivider />
                    <DropdownButton onClick={handleLogout}>
                      Sign Out
                    </DropdownButton>
                  </Dropdown>
                </UserMenu>
              ) : (
                <>
                  <Button as={Link} to="/demo" variant="secondary" size="small">
                    Try Demo
                  </Button>
                  <Button as={Link} to="/get-started" size="small">
                    Get Started
                  </Button>
                </>
              )}
            </Actions>
          </Nav>
        </HeaderContent>
      </Container>
    </HeaderWrapper>
  );
};